/* ----------------------------------------------------
   FULL INDEX.JS — PDF CASE STUDIES ENABLED
-----------------------------------------------------*/

import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import documoreLogo from "./documore-logo.png";
import Blogs from "./blogs";
import BlogPart0 from "./blogs/BlogPart0";
import BlogPart1 from "./blogs/BlogPart1";
import BlogPart2 from "./blogs/BlogPart2";
import BlogPart3 from "./blogs/BlogPart3";

import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Outlet,
  useLocation
} from "react-router-dom";


/* =======================================================
   NAVIGATION + LAYOUT
=======================================================*/
function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [caseDropdown, setCaseDropdown] = useState(false);

  const dropdownRef = useRef(null);
  const location = useLocation();

  

  /* Close dropdown on route change */
  useEffect(() => {
    setCaseDropdown(false);
  }, [location.pathname]);


  /* Click outside closes dropdown */
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCaseDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);

  }, []);


  /* Any key closes dropdown */
  useEffect(() => {
    function handleKey() {
      setCaseDropdown(false);
    }

    document.addEventListener("keydown", handleKey);

    return () =>
      document.removeEventListener("keydown", handleKey);

  }, []);


  return (
    <div className="min-h-screen flex flex-col">


      {/* ================= TOP NAV ================= */}
      <nav className="bg-[#0d2327] border-b border-[#0d2327]/50 sticky top-0 z-50">

        <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">


          {/* LOGO */}
          <Link to="/" className="flex items-center gap-3">

            <img
              src={documoreLogo}
              alt="Documore"
              className="h-8 w-8"
            />

            <span className="text-white text-lg font-semibold">
              Documore
            </span>

          </Link>


          {/* ================= DESKTOP NAV ================= */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium">


            <Link
              to="/"
              className="text-gray-300 hover:text-white transition"
            >
              Home
            </Link>

            <Link to="/blogs" className="text-gray-300 hover:text-white transition">Blogs</Link>


            {/* ============ CASE STUDIES DROPDOWN ============ */}
            <div className="relative" ref={dropdownRef}>


              <button
                onClick={() => setCaseDropdown(!caseDropdown)}
                className="text-gray-300 hover:text-white flex items-center gap-1 transition"
              >
                Case Studies

                <svg
                  className="w-4 h-4"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>

              </button>


              {/* ============ DROPDOWN CONTENT ============ */}
              {caseDropdown && (

                <div className="absolute right-0 mt-3 w-80 bg-white shadow-xl border rounded-lg py-3 z-50">


                  <h4 className="px-4 pb-2 text-[11px] font-bold tracking-widest text-gray-500 uppercase">
                    Case Studies (PDF)
                  </h4>


                  
                  <a
                    href="/case-studies/bet_validation_case_study.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCaseDropdown(false)}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                  >
                    Betting / Quant - Bet Accuracy
                  </a>


                  
                  <a
                    href="/case-studies/logistics_case_study.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCaseDropdown(false)}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                  >
                    Logistics - Overbilling Prevention
                  </a>


                  
                  <a
                    href="/case-studies/revenue_ops_case_study.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCaseDropdown(false)}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                  >
                     RevOps Integrity
                  </a>


                  <a
                    href="/case-studies/ai_response_validation_case_study.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCaseDropdown(false)}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100"
                  >
                     AI Hallucinations - Response Accuracy
                  </a>


                </div>
              )}

            </div>


            {/* CONTACT BUTTON */}
            <a
              href="/#contact"
              className="px-4 py-2 bg-white text-[#0d2327] font-semibold rounded-lg hover:bg-gray-200"
            >
              Contact
            </a>

           


            

          </div>


          {/* ================= MOBILE MENU ================= */}
          <div className="md:hidden relative">


            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-gray-200 hover:text-white"
            >

              <svg
                className="h-7 w-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {menuOpen ? (

                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />

                ) : (

                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12h18M3 6h18M3 18h18"
                  />

                )}
              </svg>

            </button>


            {menuOpen && (

              <div className="absolute right-0 mt-2 w-72 bg-[#1b3a41] text-white border border-[#2a4f55] rounded-lg shadow-xl p-3 space-y-2 z-50">


                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 hover:bg-[#244d53] rounded"
                >
                  Home
                </Link>

                <Link
  to="/blogs"
  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 hover:bg-[#244d53] rounded"
>
  Blogs
</Link>

                


                <details className="px-3 py-2">

                  <summary className="cursor-pointer font-semibold">
                    Case Studies
                  </summary>

                  <div className="mt-2 ml-2 space-y-1 text-sm">


                   


                    <a
                      href="/case-studies/bet_validation_case_study.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block py-1 hover:bg-[#244d53] rounded"
                    >
                      Betting / Quant - Bet Accuracy
                    </a>


                    <a
                      href="/case-studies/logistics_case_study.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block py-1 hover:bg-[#244d53] rounded"
                    >
                      Logistics - Overbilling Prevention
                    </a>

                     <a
                      href="/case-studies/revenue_ops_case_study.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block py-1 hover:bg-[#244d53] rounded"
                    >
                      RevOps Integrity
                    </a>

                     <a
                      href="/case-studies/ai_response_validation_case_study.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block py-1 hover:bg-[#244d53] rounded"
                    >
                      AI Hallucinations - Response Accuracy
                    </a>


                  </div>

                </details>


                <a
                  href="/#contact"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 bg-[#2e6b74] hover:bg-[#38818c] font-semibold text-center rounded"
                >
                  Contact
                </a>

              </div>
            )}

          </div>

        </div>
      </nav>


      <Outlet />

    </div>
  );
}


/* ==================== 404 PAGE ==================== */
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center p-10">
      <h1 className="text-3xl text-gray-800 font-semibold">
        404 — Page Not Found
      </h1>
    </div>
  );
}


/* ==================== ROOT ==================== */
const root = ReactDOM.createRoot(
  document.getElementById("root")
);


root.render(
  <React.StrictMode>

    <BrowserRouter>

      <Routes>

        <Route path="/" element={<Layout />}>

          <Route index element={<App />} />

          <Route path="blogs" element={<Blogs />} />

          <Route path="blogs/0" element={<BlogPart0 />} />
          <Route path="blogs/1" element={<BlogPart1 />} />
          <Route path="blogs/2" element={<BlogPart2 />} />
          <Route path="blogs/3" element={<BlogPart3 />} />

          <Route path="*" element={<NotFound />} />

        </Route>

      </Routes>

    </BrowserRouter>

  </React.StrictMode>
);

reportWebVitals();
